import { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { Button, Box } from '@mui/material';
import { sub, parseISO } from 'date-fns';
import { Alert } from '@mui/material';
import useSimpleMuiTableBuilder from './useSimpleMuiTableBuilder';
import useAppNavigation from './useAppNavigation';
import {
  getIntegrationHealthAlertDisplay,
  convertISODateToRegularDateString,
  formatIntegrationHealthDateParams,
} from 'utils';
import { HeadCell, TableBuilderCellProps, IntegrationHealth, FilterType } from 'types';

import { selectIntegrationHealthTableData } from 'selectors';
import { AppStateType } from 'reducers';
import { integrationHealthIsoStateIds } from '@constants';

import { fetchIntegrationHealth } from 'thunk';
import { integrationHealthIsoStateModifiers } from 'IntegrationModule/utils';

const integrationHealthByIdTableHeadCellIds = {
  ID: 'id',
  DATE: 'date',
  REQUEST_COUNT: 'requestCount',
  SUCCESS_COUNT: 'successCount',
  RATIO: 'ratio',
};

type FormattedIntegrationHealth = IntegrationHealth & {
  ratio: number;
  ratioLabel: string;
};

const integrationHealthByIdTableCellProps: TableBuilderCellProps = {
  headCells: [
    // {
    //   id: integrationHealthByIdTableHeadCellIds.ID,
    //   label: "ID",
    // },
    {
      id: integrationHealthByIdTableHeadCellIds.DATE,
      label: 'Date',
    },
    {
      id: integrationHealthByIdTableHeadCellIds.SUCCESS_COUNT,
      label: 'Success',
    },
    {
      id: integrationHealthByIdTableHeadCellIds.REQUEST_COUNT,
      label: 'Requests Made',
    },
    {
      id: integrationHealthByIdTableHeadCellIds.RATIO,
      label: 'Ratio (Success / Request)',
    },
  ],
  cellRenderer: {
    // [integrationHealthByIdTableHeadCellIds.ID]: ({ id }: IntegrationHealth) =>
    //   id, // Can use to test sorting
    [integrationHealthByIdTableHeadCellIds.DATE]: ({ date }: FormattedIntegrationHealth) =>
      convertISODateToRegularDateString(date),
    [integrationHealthByIdTableHeadCellIds.REQUEST_COUNT]: ({
      requestCount,
    }: FormattedIntegrationHealth) => requestCount,
    [integrationHealthByIdTableHeadCellIds.SUCCESS_COUNT]: ({
      successCount,
    }: FormattedIntegrationHealth) => successCount,
    [integrationHealthByIdTableHeadCellIds.RATIO]: ({
      ratio,
      ratioLabel,
    }: FormattedIntegrationHealth) => (
      <Box
        sx={{
          width: '12rem',
        }}
      >
        <Alert severity={getIntegrationHealthAlertDisplay(ratio * 100)}>{ratioLabel}</Alert>
      </Box>
    ),
  },
};

type HookProps = {
  hideShowAll?: boolean;
  targetService: string;
  targetServiceId: number;
  teamId: number;
  hidePagination?: boolean;
};

const daysApart = 7;

const useIntegrationHealthTable = ({
  hideShowAll,
  targetService,
  targetServiceId,
  teamId,
  hidePagination,
}: HookProps) => {
  const { navigateToIntegrationHealth } = useAppNavigation();
  const dispatch = useAppDispatch();
  const { data, filter, isRequesting } = useAppSelector((state: AppStateType) =>
    selectIntegrationHealthTableData(state, {
      isoStateId: integrationHealthIsoStateIds.IntegrationHealthTable,
      targetService,
      targetServiceId,
    }),
  );
  // Initial value is 1 because assuming the first fetch has already done outside of this hook
  const [offset, setOffset] = useState(1);

  const renderOptions = useCallback(() => {
    return (
      <Box
        sx={{
          textAlign: 'right',
          marginRight: '1rem',
        }}
      >
        {!hideShowAll && (
          <Button
            onClick={() => {
              navigateToIntegrationHealth({
                targetService,
                targetServiceId,
                teamId,
              });
            }}
            color="primary"
            variant="outlined"
          >
            Show All
          </Button>
        )}
      </Box>
    );
  }, [hideShowAll, navigateToIntegrationHealth, targetService, targetServiceId, teamId]);

  const fetchMore = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const nextStartDate = formatIntegrationHealthDateParams(
        sub(new Date(), { days: offset * daysApart }),
      );
      const nextEndDate = formatIntegrationHealthDateParams(
        sub(parseISO(nextStartDate), { days: daysApart }),
      );

      setOffset((prev) => prev + 1);

      dispatch(
        fetchIntegrationHealth({
          targetService,
          targetServiceIds: [targetServiceId],
          serviceNames: [targetService],
          startDate: nextStartDate,
          endDate: nextEndDate,
          isoStateId: integrationHealthIsoStateIds.IntegrationHealthTable,
          modifier:
            integrationHealthIsoStateModifiers[integrationHealthIsoStateIds.IntegrationHealthTable],
          //
        }),
      );
    },
    [dispatch, targetService, targetServiceId, offset],
  );

  const renderCustomPagination = useCallback(() => {
    return (
      <Button onClick={fetchMore} color="primary" disabled={isRequesting}>
        Load More
      </Button>
    );
  }, [fetchMore, isRequesting]);

  const { TableComponent } = useSimpleMuiTableBuilder({
    isLoading: isRequesting,
    filter,
    flatList: data,
    cellProps: integrationHealthByIdTableCellProps,
    headerProps: {
      tableTitle: 'Integration Status',
      renderOptions,
    },
    paginationProps: {
      hidePagination,
      renderCustomPagination,
    },
  });

  return {
    TableComponent,
  };
};

export default useIntegrationHealthTable;
